'use client';

import {
  ComponentPropsWithoutRef,
  Fragment,
  ReactNode,
  PointerEvent,
} from 'react';
import * as Headless from '@headlessui/react';
import clsx from 'clsx';

import { XIcon } from '../external-libs';

import { Button } from './button';

/**
 * A styled listbox component for single-selection menus.
 */
export function Listbox<T>({
  className,
  placeholder,
  autoFocus,
  'aria-label': ariaLabel,
  children: options,
  onClear,
  value,
  ...props
}: {
  /** Optional CSS class name. */
  className?: string;
  /** Placeholder text to display when no option is selected. */
  placeholder?: ReactNode;
  /** Whether the listbox should automatically receive focus. */
  autoFocus?: boolean;
  /** Accessible label for the listbox. */
  'aria-label'?: string;
  /** The list of options to display. */
  children?: ReactNode;
  /** Callback function to clear the selected value. */
  onClear?: () => void;
  /** The currently selected value. */
  value?: T | null | undefined;
} & Omit<Headless.ListboxProps<typeof Fragment, T>, 'as' | 'multiple'>) {
  const handleClearSelect = (e: PointerEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    onClear?.();
  };

  const showOptionsSelectIcon = (value != null && !onClear) || value == null;

  return (
    <Headless.Listbox {...props} multiple={false} value={value}>
      <Headless.ListboxButton
        aria-label={ariaLabel}
        autoFocus={autoFocus}
        className={clsx([
          className,
          // Basic layout
          'group relative block w-full transition duration-100 ease-out',
          // Background color + shadow applied to inset pseudo element, so shadow
          // blends with border in light mode
          'before:absolute before:inset-px before:rounded-[calc(var(--radius-lg)-1px)]',
          'before:bg-white before:shadow-sm',
          'dark:before:hidden',
          // Hide default focus styles
          'focus:outline-hidden',
          // Focus ring
          'data-focus:after:ring-primary-500 after:pointer-events-none',
          'after:absolute after:inset-0 after:rounded-lg after:ring-transparent',
          'data-focus:after:ring-2 after:ring-inset',
          // Disabled state
          'data-disabled:opacity-50 data-disabled:before:bg-gray-950/5',
          'data-disabled:before:shadow-none',
        ])}
        data-slot="control"
      >
        <Headless.ListboxSelectedOption
          as="span"
          className={clsx([
            // Basic layout
            'relative block w-full appearance-none rounded-lg py-[calc(--spacing(2.5)-1px)]',
            'transition duration-100 ease-out sm:py-[calc(--spacing(1.5)-1px)]',
            // Set minimum height for when no value is selected
            'min-h-10 sm:min-h-9',
            // Horizontal padding - increase right padding on small screens
            // when clear button is present
            'pl-[calc(--spacing(3.5)-1px)]',
            onClear && value != null
              ? 'pr-[calc(--spacing(9)-1px)] sm:pr-[calc(--spacing(7)-1px)]'
              : 'pr-[calc(--spacing(7)-1px)]',
            'sm:pl-[calc(--spacing(3)-1px)]',
            // Typography
            'text-start text-base/6 text-gray-950 placeholder:text-gray-500',
            'forced-colors:text-[CanvasText] dark:text-white sm:text-sm/6',
            // Truncate text to prevent overflow
            'truncate',
            // Border
            'group-data-active:border-gray-950/20 border border-gray-950/10',
            'group-data-hover:border-gray-950/20 dark:border-white/10',
            'dark:group-data-active:border-white/20 dark:group-data-hover:border-white/20',
            // Background color
            'bg-transparent dark:bg-white/5',
            // Invalid state
            'group-data-invalid:border-red-500 group-data-hover:group-data-invalid:border-red-500',
            'dark:group-data-invalid:border-red-600 dark:data-hover:group-data-invalid:border-red-600',
            // Disabled state
            'group-data-disabled:border-gray-950/20 group-data-disabled:opacity-100',
            'dark:group-data-disabled:border-white/15 dark:group-data-disabled:bg-white/[2.5%]',
            'dark:group-data-disabled:data-hover:border-white/15',
          ])}
          options={options}
          placeholder={
            placeholder && (
              <span className="block truncate text-gray-500">
                {placeholder}
              </span>
            )
          }
        />
        <span className="absolute inset-y-0 right-0 flex items-center pr-2 sm:pr-1">
          {showOptionsSelectIcon ? (
            <svg
              aria-hidden="true"
              className="
              size-5 group-data-disabled:stroke-gray-600 sm:size-4
              forced-colors:stroke-[CanvasText] stroke-gray-500 dark:stroke-gray-400
            "
              fill="none"
              viewBox="0 0 16 16"
            >
              <path
                d="M5.75 10.75L8 13L10.25 10.75"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
              />
              <path
                d="M10.25 5.25L8 3L5.75 5.25"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
              />
            </svg>
          ) : null}

          {value != null && onClear && (
            <Button
              className="pointer-events-auto z-20 px-0"
              onPointerDown={handleClearSelect}
              size="icon"
              title="Clear the filters"
              type="button"
              variant="ghost"
            >
              <XIcon className="size-4 text-body-text" />
            </Button>
          )}
        </span>
      </Headless.ListboxButton>

      <Headless.ListboxOptions
        anchor="selection start"
        className={clsx(
          // Anchor positioning
          '[--anchor-offset:-1.625rem] [--anchor-padding:--spacing(4)] sm:[--anchor-offset:-1.375rem]',
          // Base styles
          'isolate w-max min-w-[calc(var(--button-width)+1.75rem)] select-none scroll-py-1 rounded-xl p-1',
          'transition duration-100 ease-out',
          // Invisible border that is only visible in `forced-colors` mode for
          // accessibility purposes
          'focus:outline-hidden outline outline-transparent',
          // Handle scrolling when menu won't fit in viewport
          'overflow-y-scroll overscroll-contain',
          // Popover background
          'bg-white/75 backdrop-blur-xl dark:bg-gray-800/75',
          // Shadows
          'shadow-lg ring-1 ring-gray-950/10 dark:ring-inset dark:ring-white/10',
          // Transitions
          'data-closed:data-leave:opacity-0 transition-opacity duration-100 ease-in',
          'data-transition:pointer-events-none'
        )}
        transition
      >
        {options}
      </Headless.ListboxOptions>
    </Headless.Listbox>
  );
}

/**
 * An individual option within a listbox.
 */
export function ListboxOption<T>({
  children,
  className,
  ...props
}: { 
  /** Optional CSS class name. */
  className?: string; 
  /** The content of the option. */
  children?: ReactNode 
} & Omit<
  Headless.ListboxOptionProps<'div', T>,
  'as' | 'className'
>) {
  const sharedClasses = clsx(
    // Base
    'flex min-w-0 items-center transition duration-100 ease-out',
    // Icons
    '*:data-[slot=icon]:size-5 *:data-[slot=icon]:shrink-0 sm:*:data-[slot=icon]:size-4',
    '*:data-[slot=icon]:text-gray-500 group-data-focus/option:*:data-[slot=icon]:text-white',
    'dark:*:data-[slot=icon]:text-gray-400',
    'forced-colors:*:data-[slot=icon]:text-[CanvasText]',
    'forced-colors:group-data-focus/option:*:data-[slot=icon]:text-[Canvas]',
    // Avatars
    '*:data-[slot=avatar]:-mx-0.5 *:data-[slot=avatar]:size-6 sm:*:data-[slot=avatar]:size-5'
  );

  return (
    <Headless.ListboxOption as={Fragment} {...props}>
      {({ selectedOption }) => {
        if (selectedOption) {
          return (
            <div className={clsx(className, sharedClasses)}>{children}</div>
          );
        }

        return (
          <div
            className={clsx(
              // Basic layout
              'group/option grid cursor-default grid-cols-[--spacing(5)_1fr] transition duration-300 ease-out',
              'items-baseline gap-x-2 rounded-lg py-2.5 pl-2 pr-3.5',
              'sm:grid-cols-[--spacing(4)_1fr] sm:py-1.5 sm:pl-1.5 sm:pr-3',
              // Typography
              'text-base/6 text-gray-950 dark:text-white sm:text-sm/6',
              'forced-colors:text-[CanvasText]',
              // Focus
              // 'data-focus:bg-primary-500/10 outline-hidden data-focus:text-white',
              'data-focus:bg-primary-500/10 data-focus:text-primary-700 outline-hidden cursor-pointer transition',
              'dark:data-focus:text-primary-400 ',
              // Forced colors mode
              'forced-color-adjust-none forced-colors:data-focus:bg-[Highlight]',
              'forced-colors:data-focus:text-[HighlightText]',
              // Disabled
              'data-disabled:opacity-50'
            )}
          >
            <svg
              aria-hidden="true"
              className="
                size-5 group-data-selected/option:inline sm:size-4 relative hidden
                self-center stroke-current
              "
              fill="none"
              viewBox="0 0 16 16"
            >
              <path
                d="M4 8.5l3 3L12 4"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
              />
            </svg>
            <span className={clsx(className, sharedClasses, 'col-start-2')}>
              {children}
            </span>
          </div>
        );
      }}
    </Headless.ListboxOption>
  );
}

/**
 * A label for a listbox option.
 */
export function ListboxLabel({
  className,
  ...props
}: ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      {...props}
      className={clsx(
        className,
        'ml-2.5 truncate transition duration-100 ease-out first:ml-0 sm:ml-2 sm:first:ml-0'
      )}
    />
  );
}

/**
 * A secondary description for a listbox option.
 */
export function ListboxDescription({
  children,
  className,
  ...props
}: ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      {...props}
      className={clsx(
        className,
        'group-data-focus/option:text-white flex flex-1 overflow-hidden text-gray-500',
        'transition duration-100 ease-out',
        'before:w-2 before:min-w-0 before:shrink dark:text-gray-400'
      )}
    >
      <span className="flex-1 truncate">{children}</span>
    </span>
  );
}
